import * as PapiService from 'src/services/api/papiService';
import { UserDetailsFromCorpSystem } from 'src/types/admin/accessManagement.types';
import {
  EmployeeSearchBasicInfo,
  EmployeeSearchResults,
  FinTechPAPISearchEmployeesResponse,
  SearchEmployeesRequest,
  SortField,
} from 'src/types/admin/advancedSearchResponseStructure';
import {
  EmployeeBasicInfo,
  FinTechPAPIGetEmployeeLoginDetailsResponse,
} from 'src/types/admin/employeeLoginResponseStructure';
import { logger } from './logger';

// ============================================================================
// TYPES & ENUMS
// ============================================================================

export interface ValidateUserAliasResponse {
  isValid: boolean;
  validationMessage: string;
  userInfo?: UserDetailsFromCorpSystem;
  success: boolean;
  error?: Error | unknown;
}

export enum UserValidationMessages {
  UserIsValid = 'User is valid',
  UserIsInactive = 'User is inactive',
  UserAliasIsInvalid = 'User alias is invalid',
}

export enum EmployeeJobStatus {
  Active = 'A',
  Inactive = 'I',
}

// ============================================================================
// TRANSFORMATION FUNCTIONS
// ============================================================================

/**
 * Type guard to check if input is EmployeeBasicInfo
 */
const isEmployeeBasicInfo = (input: EmployeeBasicInfo | EmployeeSearchBasicInfo): input is EmployeeBasicInfo => {
  return 'legacyEmployeeId' in input && 'badgeColor' in input;
};

/**
 * Generic transformation function for converting PAPI employee data to UserDetailsFromCorpSystem format.
 * Handles both EmployeeBasicInfo and EmployeeSearchBasicInfo types.
 *
 * @param employee - Employee data from PAPI (either basic info or search result)
 * @returns Transformed user details in DB format
 */
export const transformEmployeeToUserDetails = (
  employee: EmployeeBasicInfo | EmployeeSearchBasicInfo
): UserDetailsFromCorpSystem => {
  const baseTransformation = {
    person_id: employee.personId,
    job_status: employee.jobStatus,
    first_name: employee.firstName,
    last_name: employee.lastName,
    login: employee.login,
    business_title: employee.businessTitle,
    cost_center_name: employee.costCenterName,
  };

  if (isEmployeeBasicInfo(employee)) {
    return {
      ...baseTransformation,
      legacy_employee_id: employee.legacyEmployeeId,
      badge_color: employee.badgeColor,
      employee_status: employee.employeeStatus,
    };
  }

  return {
    ...baseTransformation,
    legacy_employee_id: '', // Not available in search results
    badge_color: '', // Not available in search results
    employee_status:
      employee.employeeStatus === EmployeeJobStatus.Active ? EmployeeJobStatus.Active : EmployeeJobStatus.Inactive,
  };
};

/**
 * Transforms PAPI user info into validation response format.
 *
 * @param userInfo - User information from PAPI
 * @param isValid - Whether the user is valid/active
 * @returns Formatted validation response
 */
const transformToValidationResponse = (
  userInfo: FinTechPAPIGetEmployeeLoginDetailsResponse,
  isValid: boolean
): ValidateUserAliasResponse => {
  return {
    isValid,
    validationMessage: isValid ? UserValidationMessages.UserIsValid : UserValidationMessages.UserIsInactive,
    userInfo: transformEmployeeToUserDetails(userInfo.employee.basicInfo),
    success: true,
  };
};

// ============================================================================
// VALIDATION FUNCTIONS
// ============================================================================

/**
 * Validates if a user is active based on their job status.
 *
 * @param userInfo - User information from PAPI
 * @returns True if user is active, false otherwise
 */
const isUserActive = (userInfo: FinTechPAPIGetEmployeeLoginDetailsResponse): boolean => {
  return userInfo.employee.basicInfo.jobStatus === EmployeeJobStatus.Active;
};

/**
 * Validates an employee login with PAPI and returns formatted response.
 * This is the main validation function used across the application.
 *
 * @param employeeLogin - Employee login/alias to validate
 * @returns Promise resolving to validation response with user details
 *
 * @example
 * const result = await validateEmployeeLoginWithPAPI('jdoe');
 * if (result.success && result.isValid) {
 *   console.log('User is valid:', result.userInfo);
 * }
 */
export const validateEmployeeLoginWithPAPI = async (employeeLogin: string): Promise<ValidateUserAliasResponse> => {
  try {
    const papiResponse = await PapiService.getEmployeeByLogin(employeeLogin);
    const isValid = isUserActive(papiResponse);
    return transformToValidationResponse(papiResponse, isValid);
  } catch (error: unknown) {
    logger.error('Error validating employee login:', { employeeLogin, error });
    return {
      isValid: false,
      validationMessage: UserValidationMessages.UserAliasIsInvalid,
      success: false,
      error,
    };
  }
};

// ============================================================================
// SEARCH FUNCTIONS WITH DEFAULT PARAMETERS
// ============================================================================

/**
 * Sorts employees based on custom business rules:
 * 1. Employees with costCenterId "8499" come first
 * 2. Then employees with workerSubtypeV2 "F" come first
 * 3. Remaining employees maintain their original order
 *
 * @param employees - Array of employee search results to sort
 * @returns Sorted array of employees
 */
const sortEmployeesByBusinessRules = (employees: EmployeeSearchResults[]): EmployeeSearchResults[] => {
  return [...employees].sort((a, b) => {
    // Priority 1: costCenterId "8499" comes first
    const aCostCenter = a.basicInfo?.costCenterId;
    const bCostCenter = b.basicInfo?.costCenterId;

    if (aCostCenter === '8499' && bCostCenter !== '8499') return -1;
    if (aCostCenter !== '8499' && bCostCenter === '8499') return 1;

    // Priority 2: workerSubtypeV2 "F" comes first (within same costCenter priority)
    const aWorkerSubtype = a.job?.workerSubtypeV2;
    const bWorkerSubtype = b.job?.workerSubtypeV2;

    if (aWorkerSubtype === 'F' && bWorkerSubtype !== 'F') return -1;
    if (aWorkerSubtype !== 'F' && bWorkerSubtype === 'F') return 1;

    // Maintain original order for equal priority
    return 0;
  });
};

/**
 * Searches for active employees by name with default parameters.
 * Provides a simplified interface for common search operations.
 * Results are sorted with costCenterId "8499" first, then workerSubtypeV2 "F" first.
 *
 * @param name - Name to search for (minimum 2 characters)
 * @param size - Number of results to return (default: 10)
 * @returns Promise resolving to search results
 *
 * @example
 * const results = await searchActiveEmployeesByName('John Doe');
 * results.employees.forEach(emp => console.log(emp.displayName));
 */
export const searchEmployeesByName = async (
  searchTerm: string,
  activeOnly: boolean = true,
  size: number = 50,
  sortBy: SortField = 'WEIGHT_THEN_LOGIN'
): Promise<FinTechPAPISearchEmployeesResponse> => {
  const requestBody: SearchEmployeesRequest = {
    name: searchTerm,
    activeOnly: activeOnly,
    size: size,
    sortBy: sortBy,
  };
  const response = await PapiService.searchEmployees(requestBody);

  // Apply custom sorting rules
  return {
    ...response,
    employees: sortEmployeesByBusinessRules(response.employees),
  };
};

/**
 * Searches for active employees with advanced query.
 * Automatically adds active employee filter.
 * Results are sorted with costCenterId "8499" first, then workerSubtypeV2 "F" first.
 *
 * @param searchTerm - Search term for name
 * @param size - Number of results to return (default: 10)
 * @param sortBy - Sort field (default: WEIGHT_THEN_LOGIN)
 * @returns Promise resolving to search results
 *
 * @example
 * const results = await searchActiveEmployees('John', 20, 'FIRST_NAME');
 */
export const searchEmployeesByLogin = async (
  searchTerm: string,
  activeOnly: boolean = true,
  size: number = 10,
  sortBy: SortField = 'WEIGHT_THEN_LOGIN'
): Promise<FinTechPAPISearchEmployeesResponse> => {
  const requestBody: SearchEmployeesRequest = {
    login: searchTerm,
    activeOnly: activeOnly,
    size: size,
    sortBy: sortBy,
  };
  const response = await PapiService.searchEmployees(requestBody);

  // Apply custom sorting rules
  return {
    ...response,
    employees: sortEmployeesByBusinessRules(response.employees),
  };
};

/**
 * Gets employee details by login and transforms to UserDetailsFromCorpSystem format.
 * Convenience function that combines fetch and transform operations.
 *
 * @param login - Employee login/alias
 * @returns Promise resolving to transformed user details
 *
 * @example
 * const userDetails = await getEmployeeDetails('jdoe');
 * console.log(userDetails.first_name, userDetails.last_name);
 */
export const getEmployeeDetails = async (login: string): Promise<UserDetailsFromCorpSystem> => {
  const response = await PapiService.getEmployeeByLogin(login);
  return transformEmployeeToUserDetails(response.employee.basicInfo);
};

export const getEmployeeDetailsRaw = async (login: string): Promise<FinTechPAPIGetEmployeeLoginDetailsResponse> => {
  const response = await PapiService.getEmployeeByLogin(login);
  return response;
};

/**
 * Checks if an employee is active without full validation response.
 * Lightweight check for active status only.
 *
 * @param login - Employee login/alias
 * @returns Promise resolving to boolean indicating if employee is active
 *
 * @example
 * const isActive = await isEmployeeActive('jdoe');
 * if (isActive) {
 *   // Proceed with active employee logic
 * }
 */
export const isEmployeeActive = async (login: string): Promise<boolean> => {
  try {
    const response = await PapiService.getEmployeeByLogin(login);
    return response.employee.basicInfo.jobStatus === EmployeeJobStatus.Active;
  } catch (error: unknown) {
    logger.error('Error checking employee active status:', { login, error });
    return false;
  }
};
